'use client';

import React, { useState } from 'react';
import FrWeddingNav from '@/components/FrWeddingNav';
import { getTranslations } from '@/lib/i18n';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

const faqsFr: FAQItem[] = [
  {
    id: 'faq-1',
    question: "Je m'inquiète de savoir comment me déplacer entre les différents lieux ?",
    answer: "Ne vous inquiétez pas, il y a toujours un moyen. Envoyez-nous simplement un message et nous vous aiderons à tout organiser pour que vous puissiez vous déplacer facilement et profiter de la journée.",
  },
  {
    id: 'faq-2',
    question: "Je viens seul(e) et je ne connaîtrai personne…",
    answer: "C'est un rassemblement très petit, chaleureux et convivial. Vous vous sentirez toujours entouré(e), et nous veillerons à ce que tout le monde se sente inclus dès le début.",
  },
  {
    id: 'faq-3',
    question: "Cela semble être une longue journée, y aura-t-il un endroit pour se reposer ou pour que les enfants aillent se coucher tôt ?",
    answer: "Oui, absolument. Nous avons réservé deux chambres à l'écart des festivités pour quiconque aurait besoin d'un moment pour se retirer, se reposer ou coucher les petits.",
  },
  {
    id: 'faq-4',
    question: "J'ai vu le thème indigo, mais je n'ai rien qui corresponde ?",
    answer: "Le thème est simplement là pour rassembler le tout de manière détendue et expressive. Ne vous sentez pas obligé(e) d'acheter quoi que ce soit de nouveau, réutilisez, empruntez, recyclez ou créez si vous le souhaitez. L'indigo embrasse toutes les nuances de bleu imaginables, dans la quantité qui vous convient.",
  },
  {
    id: 'faq-5',
    question: "J'ai un costume bleu marine formel… doit-il être aussi formel ?",
    answer: "Pas du tout. C'est un mariage en plein été avec une atmosphère détendue mais raffinée. Le lin, la coupe plus souple et les silhouettes naturelles et fluides sont tout à fait en accord avec l'atmosphère que nous aimerions créer.",
  },
  {
    id: 'faq-6',
    question: "Je ne suis pas végétalien(ne), qu'est-ce que je vais manger ?",
    answer: "Il y aura beaucoup de variété pour tout le monde. Notre objectif est de créer quelque chose de généreux et inclusif, vous serez donc très bien servi(e).",
  },
  {
    id: 'faq-7',
    question: "Je ne bois pas d'alcool, y aura-t-il quelque chose pour moi ?",
    answer: "Absolument, il y aura une abondance d'options sans alcool, y compris beaucoup de mocktails. S'il y a quelque chose que vous appréciez particulièrement ou que vous recommanderiez, faites-le nous savoir, nous adorerions l'inclure.",
  },
  {
    id: 'faq-8',
    question: "Que se passe-t-il s'il pleut ?",
    answer: "Alors cela fait partie de l'atmosphère. Il y aura toujours un endroit chaud, couvert et accueillant. Si vous apportez des couches supplémentaires pour le temps imprévisible, nous avons beaucoup d'espace vestiaire pour tout accueillir.",
  },
  {
    id: 'faq-9',
    question: "J'ai regardé la régate et il y a des choses que j'adorerais voir, mais elles se chevauchent avec le mariage…",
    answer: "Ne vous inquiétez pas, prenez le temps de profiter de ce que Dartmouth a à offrir. Nous avons choisi le week-end de la régate parce que nous voulions partager son atmosphère et sa vivacité avec vous, alors n'hésitez pas à en profiter pleinement.",
  },
  {
    id: 'faq-10',
    question: "Je prévois de rester et de profiter des activités après dimanche soir jusqu'à lundi, mais je n'ai rien vu à ce sujet ?",
    answer: "Nous avons gardé cette partie un peu secrète pour que personne ne se sente exclu. Dites-nous combien de temps vous restez, et nous pourrons continuer à nous amuser ensemble.",
  },
  {
    id: 'faq-11',
    question: "J'aimerais aider ou m'impliquer, mais je ne suis pas professionnel(le), que pourrais-je faire ?",
    answer: "Toute aide est chaleureusement accueillie. Que ce soit la pâtisserie, la photographie, les arts et l'artisanat, les cocktails ou mocktails, ou simplement avoir un œil pour les détails, nous serions ravis de vous entendre.",
  },
  {
    id: 'faq-12',
    question: "J'ai un talent que j'aimerais partager, ou je voudrais faire un discours, mais je suis un peu timide…",
    answer: "Parlez-nous-en. Nous serions vraiment ravis d'en entendre parler et de voir comment nous pouvons l'inclure d'une manière qui vous convient et qui soit spéciale pour vous.",
  },
];

export default function FrFAQPage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const t = getTranslations('fr');

  const toggle = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <main className="bg-buttermilk min-h-screen">
      <FrWeddingNav />

      <section className="relative w-full overflow-hidden">
        <img
          src="/assets/images/IMG_1113-1777579917181.jpg"
          alt="Image héro du mariage"
          className="w-full h-auto block"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-deep-navy/5 to-deep-navy/65" />
        <div className="absolute bottom-0 left-0 right-0 z-10 px-8 md:px-16 xl:px-24 pb-10 lg:pb-16 max-w-screen-2xl mx-auto w-full">
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-warm-sand/80 mb-4">
            {t.faq.heroLabel}
          </p>
          <h1 className="font-serif text-display-md text-off-white">
            {t.faq.heroTitle.split('\n').map((line, i) => (
              <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
            ))}
          </h1>
        </div>
        <p className="absolute bottom-2 right-3 z-20 font-sans text-white/70 leading-none" style={{ fontSize: '9px', letterSpacing: '0.02em' }}>
          Le ferry de Naoshima, oct. 24
        </p>
      </section>

      <section className="px-8 md:px-16 xl:px-24 pb-24 max-w-screen-2xl mx-auto">
        <div className="max-w-2xl">
          {faqsFr.map((faq, index) => {
            const isOpen = openId === faq.id;
            return (
              <div
                key={faq.id}
                className={`border-b transition-colors duration-300 ${index === 0 ? 'border-t border-stone/20' : 'border-stone/20'}`}
              >
                <button
                  onClick={() => toggle(faq.id)}
                  className="w-full text-left py-6 flex items-start justify-between gap-6 group"
                  aria-expanded={isOpen}
                >
                  <span className={`font-serif text-lg md:text-xl leading-snug transition-colors duration-300 ${isOpen ? 'text-faded-navy' : 'text-stone group-hover:text-faded-navy'}`}>
                    {faq.question}
                  </span>
                  <span className={`flex-shrink-0 mt-1 w-5 h-5 flex items-center justify-center transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-stone/60">
                      <line x1="7" y1="0" x2="7" y2="14" stroke="currentColor" strokeWidth="1.2" />
                      <line x1="0" y1="7" x2="14" y2="7" stroke="currentColor" strokeWidth="1.2" />
                    </svg>
                  </span>
                </button>
                <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="font-sans text-stone text-base leading-relaxed pb-7 pr-8">{faq.answer}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
}
